import { create } from "zustand";

/**
 * Single, minimal Zustand store for plinko (hard rule: one store per app).
 *
 *   idle → dropping (ball replays the server path) → done
 *
 * The drop is decided server-side the instant the button is hit; "dropping" only
 * paces the reveal — the ball walks the exact bounce bits the server rolled.
 */

export type Phase = "idle" | "dropping" | "done";

export interface DropResult {
  path: number[];
  bucket: number;
  mult: number;
  value?: number;
  rank?: number;
  total?: number;
  percentile?: number;
}

export interface PastDrop {
  mult: number;
}

interface PlinkoState {
  phase: Phase;
  result: DropResult | null;
  history: PastDrop[];
  /** Best landed multiplier ever (×100), localStorage-backed. */
  best: number;

  startDrop: () => void;
  land: (result: DropResult) => void;
  reset: () => void;
}

const BEST_KEY = "kp:plinko:best";
const HISTORY_KEY = "kp:plinko:history";
const HISTORY_LEN = 10;

function loadBest(): number {
  try {
    return Number.parseInt(localStorage.getItem(BEST_KEY) ?? "0", 10) || 0;
  } catch {
    return 0;
  }
}
function loadHistory(): PastDrop[] {
  try {
    const arr = JSON.parse(localStorage.getItem(HISTORY_KEY) ?? "[]") as unknown;
    return Array.isArray(arr) ? (arr as PastDrop[]).slice(0, HISTORY_LEN) : [];
  } catch {
    return [];
  }
}
function save(key: string, v: string): void {
  try {
    localStorage.setItem(key, v);
  } catch {
    /* blocked — fine */
  }
}

export const usePlinkoStore = create<PlinkoState>((set, get) => ({
  phase: "idle",
  result: null,
  history: loadHistory(),
  best: loadBest(),

  startDrop: () => set({ phase: "dropping", result: null }),

  land: (result) => {
    const history = [{ mult: result.mult }, ...get().history].slice(0, HISTORY_LEN);
    save(HISTORY_KEY, JSON.stringify(history));
    let best = get().best;
    if (result.value !== undefined && result.value > best) {
      best = result.value;
      save(BEST_KEY, String(best));
    }
    set({ phase: "done", result, history, best });
  },

  reset: () => set({ phase: "idle", result: null }),
}));
